import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import api from "../../utils/api";

const LABEL_OPTIONS = [
  { value: "actual_help", label: "Actual Fall Help Needed" },
  { value: "actual_no_help", label: "Actual Fall No Help Needed" },
  { value: "false_alarm", label: "False Alarm No Fall" },
  { value: "minor_trip", label: "Minor Slip/Trip Not a Fall" },
  { value: "unclear", label: "Unclear / Needs Review" },
  { value: "repeat_fall", label: "Repeat Fall Already Reported" },
];

const parseDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateTime = (isoString) => {
  const date = parseDate(isoString);
  return date ? date.toLocaleString() : "";
};

const isWithinLastDays = (isoString, days) => {
  const date = parseDate(isoString);
  if (!date) return false;
  const threshold = Date.now() - days * 24 * 60 * 60 * 1000;
  return date.getTime() >= threshold;
};

const isSameDay = (isoString, targetDateString) => {
  const date = parseDate(isoString);
  if (!date || !targetDateString) return false;
  const datePart = date.toISOString().slice(0, 10);
  return datePart === targetDateString;
};

const VideoGalleryPage = () => {
  const [videos, setVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [dateFilter, setDateFilter] = useState("all");
  const [specificDate, setSpecificDate] = useState("");

  const [selectedVideo, setSelectedVideo] = useState(null);
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await api.get("/videos/");
        const data = response.data || response;
        setVideos(Array.isArray(data) ? data : data?.results || []);
      } catch (err) {
        console.error("Failed to fetch videos:", err);
      }
    };
    fetchVideos();
  }, []);

  useEffect(() => {
    let filtered = [...videos];

    if (dateFilter === "24h") {
      filtered = filtered.filter((video) => isWithinLastDays(video.created_at, 1));
    } else if (dateFilter === "7d") {
      filtered = filtered.filter((video) => isWithinLastDays(video.created_at, 7));
    } else if (dateFilter === "30d") {
      filtered = filtered.filter((video) => isWithinLastDays(video.created_at, 30));
    }

    if (specificDate) {
      filtered = filtered.filter((video) => isSameDay(video.created_at, specificDate));
    }

    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    setFilteredVideos(filtered);
  }, [dateFilter, specificDate, videos]);

  const handleSaveLabel = async () => {
    if (!selectedVideo || !label) return;
    setSaving(true);
    try {
      await api.patch(`/videos/${selectedVideo.id}/`, { label });
      setVideos((prev) =>
        prev.map((video) =>
          video.id === selectedVideo.id ? { ...video, label } : video,
        ),
      );
      setSelectedVideo(null);
      setLabel("");
    } catch (err) {
      console.error("Failed to save label:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Video Gallery
      </Typography>

      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel>Filter by date</InputLabel>
          <Select
            value={dateFilter}
            label="Filter by date"
            onChange={(event) => setDateFilter(event.target.value)}
          >
            <MenuItem value="all">All Time</MenuItem>
            <MenuItem value="24h">Last 24 Hours</MenuItem>
            <MenuItem value="7d">Last 7 Days</MenuItem>
            <MenuItem value="30d">Last 30 Days</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Pick a specific date"
          type="date"
          value={specificDate}
          onChange={(event) => setSpecificDate(event.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />

        <Button
          variant="outlined"
          onClick={() => {
            setDateFilter("all");
            setSpecificDate("");
          }}
        >
          Reset Filters
        </Button>
      </Box>

      <Grid container spacing={3}>
        {filteredVideos.map((video) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={video.id}>
            <Card
              sx={{ cursor: "pointer", borderRadius: 2, boxShadow: 3 }}
              onClick={() => setSelectedVideo(video)}
            >
              <CardMedia
                component="img"
                height="180"
                image={video.thumbnail_url || "/placeholder-thumbnail.jpg"}
                alt={video.title}
              />
              <CardContent>
                <Typography variant="subtitle1" noWrap>
                  {video.title || `Video #${video.id}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDateTime(video.created_at)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={!!selectedVideo}
        onClose={() => {
          setSelectedVideo(null);
          setLabel("");
        }}
        maxWidth="md"
        fullWidth
      >
        {selectedVideo && (
          <>
            <DialogTitle>{selectedVideo.title || `Video #${selectedVideo.id}`}</DialogTitle>
            <DialogContent>
              <video
                src={selectedVideo.file_url}
                controls
                style={{ width: "100%", borderRadius: 8 }}
              />

              <Box mt={3}>
                <FormControl fullWidth>
                  <InputLabel>Choose Label</InputLabel>
                  <Select
                    value={label}
                    label="Choose Label"
                    onChange={(event) => setLabel(event.target.value)}
                  >
                    {LABEL_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setSelectedVideo(null);
                  setLabel("");
                }}
              >
                Close
              </Button>
              <Button
                onClick={handleSaveLabel}
                variant="contained"
                disabled={saving || !label}
              >
                {saving ? "Saving..." : "Save Label"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default VideoGalleryPage;
